import { Badge } from '@/components/ui/badge'
import EventAvatar from './EventAvatar'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { usePageInfo } from '../config/queries'
import { minRaidSize } from '../helpers/raidSize'
import { keyBy } from 'lodash'
import { cn } from '@/lib/utils'
import EventParticipationSummary from './EventParticipationSummary'
import { eventInstant } from '../helpers/eventInstant'
dayjs.extend(RelativeTime)

type Props = {
  eventReference: wowaudit.EventReference
} & React.HTMLAttributes<HTMLDivElement>

export const useUpcomingEventDetails = (
  eventReference: wowaudit.EventReference,
) => {
  const {
    data: { user, gameVersion },
  } = usePageInfo()

  const participants = keyBy(
    eventReference.participantReferences,
    'characterId',
  )
  const characterReference = user?.characterReferences?.find(
    (ref) => participants[ref.id.toString()],
  )

  const amountPresent = eventReference.participantReferences.filter(
    (ref) => ref.status === 'present',
  ).length

  const hasSufficientParticipants =
    amountPresent >= minRaidSize(eventReference, gameVersion)

  return {
    characterReference,
    amountPresent,
    participants,
    hasSufficientParticipants,
  }
}

const UpcomingEvent = ({ eventReference, className }: Props) => {
  const startsAt = eventInstant(
    eventReference.startsAt,
    eventReference.timezone,
  )

  return (
    <div className={cn('w-full', className)}>
      <div className='flex items-center gap-2 w-full'>
        <EventAvatar eventReference={eventReference} className='h-10 w-10' />
        <div className='w-full'>
          <div className='flex items-center justify-between gap-4'>
            <header className='capitalize group-hover:text-primary transition-colors'>
              {eventReference.title}
            </header>

            <Badge variant='outline' className={cn('capitalize')}>
              {eventReference.primaryDifficulty?.replace('_', ' ')}
            </Badge>
          </div>

          <div className='opacity-70 font-normal text-[0.7rem] mt-0 capitalize flex justify-between gap-4'>
            <span>
              {dayjs(eventReference.startsAt).format('dddd, MMM D, HH:mm')} -{' '}
              {dayjs(eventReference.endsAt).format('HH:mm')}
            </span>
          </div>
        </div>
      </div>
      <div className='mt-2 flex items-center gap-4 justify-between'>
        <EventParticipationSummary eventReference={eventReference} />

        <span className='grow text-right text-xs'>
          {startsAt.isBefore(dayjs()) ? 'started ' : ''}
          {startsAt.fromNow()}
        </span>
      </div>
    </div>
  )
}

export default UpcomingEvent
